/**
 * Shared "which FIELD_OPTIONS keys does this mapping currently resolve to" helper.
 *
 * A key counts as resolved via a plain `mappedTo` OR, for a `pattern` transform, any
 * non-null per-blank `targets` entry. A `constant` transform's value always lands on its
 * own `mappedTo`, so it's already covered by the plain check — no separate handling needed.
 *
 * Reused by the preview builder (`build-mapping-preview.ts`) and both required-field
 * checks (`mapping-phase-wizard-helpers.ts#computeMissingRequiredFieldKeys` and
 * `use-mapping-phase-state.ts`'s live `mappedSystemFieldKeys`) so the three derivations
 * can't drift apart again — the custom-supplier-import-transforms headline example
 * (splitting a combined GPS column into `latitude`/`longitude` via a pattern's per-blank
 * targets) must satisfy those required fields the same way a plain 1:1 `mappedTo` does.
 */
import type { ColumnMappingItem } from '@ooh/contracts';

/** FIELD_OPTIONS key for the supplier picker — shared between the mapping table's
 * unmapped-required check (mapping-phase-parts.tsx) and the output preview's column/error
 * suppression (build-mapping-preview.ts) so a future rename of this key can't silently
 * desync the two views. */
export const SUPPLIER_FIELD_KEY = 'supplier';

/** True when `key` is the supplier field AND the operator already covered supplier via the
 * combobox/saved mapping (`supplierCovered`) — the one shared check both the mapping table
 * and the output preview must apply identically. */
export function isSupplierFieldCovered(key: string, supplierCovered: boolean): boolean {
  return supplierCovered && key === SUPPLIER_FIELD_KEY;
}

/** FIELD_OPTIONS key for the media-type field — the `Typ nosnika` row collapses (auto-resolved
 * by the file-level type-override picker) instead of requiring a column mapping, so it must be
 * excluded from the required-field lists the same way SUPPLIER_FIELD_KEY is once covered. */
export const MEDIA_TYPE_FIELD_KEY = 'type';

/** True when `key` is the media-type field AND a file-level type override is active — mirrors
 * isSupplierFieldCovered above. Without this, "Typ nosnika" stayed listed as an unresolved
 * required field even after the operator picked a type-override value (media-import mapping UX
 * bug: the override picker and the missing-required-field validation were disconnected). */
export function isMediaTypeFieldCovered(
  key: string,
  mediaTypeOverride: string | undefined,
): boolean {
  return mediaTypeOverride != null && key === MEDIA_TYPE_FIELD_KEY;
}

function addPatternTargets(keys: Set<string>, targets: (string | null)[]): void {
  for (const target of targets) {
    if (target) keys.add(target);
  }
}

/** All FIELD_OPTIONS keys this mapping currently resolves to (row-level mappedTo or per-blank pattern targets). */
export function collectMappedFieldKeys(mappings: ColumnMappingItem[]): Set<string> {
  const keys = new Set<string>();
  for (const m of mappings) {
    if (m.transform?.type === 'pattern') {
      addPatternTargets(keys, m.transform.targets);
      continue;
    }
    if (m.mappedTo) keys.add(m.mappedTo);
  }
  return keys;
}

/**
 * Strips the ' *' required-field suffix from a FIELD_OPTIONS label.
 * FIELD_OPTIONS labels use ' *' suffix (e.g. 'Miejscowość *') while
 * adapter requiredColumns / optionalColumns use the bare NOSNIKI_HEADERS
 * label (e.g. 'Miejscowość'). Normalizing both sides enables matching.
 * Uses an exact `.endsWith(' *')` check — FIELD_OPTIONS labels are a
 * controlled constant with consistent ' *' suffix formatting.
 */
export function stripRequiredSuffix(label: string): string {
  return label.endsWith(' *') ? label.slice(0, -2) : label;
}
